import { BsCaretDown, BsCaretDownFill, BsCaretRightFill } from "react-icons/bs";
import { PropertyData } from "./PropertyInfoCollectorTypes";
import { defaultParser, titleValParser } from "./PropertyInfoParser";
import { PiCaretRightLight } from "react-icons/pi";
import { Spinner } from "react-bootstrap";
import { Localization } from "@viamap/viamap2-common";
import { MitLatLng } from "src/managers/MapFacade";
import { BiCheck } from "react-icons/bi";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { openWindow, WindowId } from "src/WindowManager/WindowState";
import { Menu } from "iconsax-react";
import { ProtectedFeature as ProtectedFeatureComponent } from "src/components/ProtectedFeature";
import { ApplicationStateContext } from "src/states/ApplicationState";
import { autoPlacement, autoUpdate, FloatingPortal, offset, Placement, useDismiss, useFloating, useHover, useInteractions, useMergeRefs } from "@floating-ui/react";
import { Fragment, isValidElement, useContext, useEffect, useId, useRef, useState } from "react";
import React from "react";
import { TrackingErrorBoundary } from "src/common/managers/TrackingErrorBoundary";

type KeyValueComponentProps<T> = {
  valKey: React.ReactNode;
  value: T;
  keepEmpty?: boolean;
  infoText?: string;
  variant?: string;
  KeyEndParser?: (key: string) => string;
  CParser?: (key: string, val: T) => string;
  _className?: string;
};

export function KeyValueTable({ children }) {
  return <table className="Table-MitProp">
    <tbody>{children}</tbody>
  </table>;
}

export function FloatingBox(props: {
  children: React.ReactNode;
  aspectRatio?: number;
}) {
  return (
    <div
      className="FloatingBox-MitProp"
      style={{ aspectRatio: props.aspectRatio || undefined }}
    >
      {props.children}
    </div>
  );
}

export function PropertyMapContainer(props: {
  children: JSX.Element[] | JSX.Element;
  aspectRatio?: number;
  location: string;
}) {
  return (

    <div
      className="MapContainer-MitProp"
      style={{ aspectRatio: props.aspectRatio || undefined }}
    >
      <TrackingErrorBoundary location={props.location} >
        {props.children}
      </TrackingErrorBoundary>
    </div>
  );
}

export function MapButton(props: {
  children: any;
  onClick?: (e: React.MouseEvent) => void;
}) {
  return (
    <div onClick={props.onClick} className="MapButton-MitProp">
      {props.children}
    </div>
  );
}

export function MapButtonContainer(props: {
  children: JSX.Element[] | JSX.Element;
}) {
  return (
    <div className="MapButtonContainer-MitProp">
      {props.children}
    </div>
  );
}

export function PropertyImageContainer(props: {
  children: React.ReactElement;
  aspectRatio?: number;
}) {
  return (
    <div
      className="ImageContainer-MitProp"
      style={{ aspectRatio: props.aspectRatio || undefined }}
    >
      {props.children}
    </div>
  );
}

/**
 * Display af længere tekster opbygget af standard html elementer
 * @param props 
 * @returns 
 */
export function PropertyTextDoc(props: {
  children: JSX.Element | JSX.Element[];
}) {
  return (
    <div className="PropertyTextDoc">
      {props.children}
    </div>
  );
}

export function TitleValueLine<T>(props: KeyValueComponentProps<T>) {
  return <KeyValueLine<T> {...props} _className="KeyValTitle" />;
}

export function KeyValueLine<T>(props: KeyValueComponentProps<T>) {
  const valKey =
    typeof props.valKey == "string"
      ? props.KeyEndParser?.(props.valKey) || titleValParser(props.valKey)
      : props.valKey;


  if (isValidElement(props.value)) {
    return (
      <div className={"Line-MitProp " + (props._className || "")}>
        <div data-info={props.infoText}>{valKey}</div>
        <div>{props.value}</div>
      </div>
    );
  }
  let resVal =
    props.CParser?.(typeof valKey == "string" ? valKey : "", props.value) ??
    defaultParser(typeof valKey == "string" ? valKey : "", props.value);
  let keepEmpty = props.keepEmpty ?? false;

  if (resVal === "" && !keepEmpty) {
    return null;
  }

  return (
    <div className={"Line-MitProp " + (props._className || "")}>
      <div data-info={props.infoText} >{valKey}</div>
      <div>{resVal}</div>
    </div>
  );
}

export function KeyValuesBoxGrid(props: {
  minWidth: `${number}px`;
  maxCols?: number;
  children: React.ReactNode;
}) {
  const maxCols = (props.maxCols ?? 9) + 1;
  return (
    <div
      className="Grid-MitProp"
      style={
        {
          "--minBoxWidth": props.minWidth,
          "--maxBoxCols": maxCols,
        } as React.CSSProperties
      }
    >
      {props.children}
    </div>
  );
}

export function EmptySpace() {
  return <div style={{ height: "1.4em" }} />;
}

export function ErrorMessage(props: {
  errors: Partial<Record<keyof PropertyData, { error: string }>>;
}) {
  let errorsIdx = Object.keys(props.errors);

  if (errorsIdx.length === 0) {
    return null;
  }

  return (
    <div className="ErrCon-MitProp">
      {errorsIdx.map((a) => (
        <SingleError key={a} error={props.errors[a]} />
      ))}
    </div>
  );
}

function VariantHandler(name: string, variant?: string): string {
  return (variant ? name + " " + name + "-" + variant : name) + " "
}

export function GlassFoldUdBox(props: {
  title: any;
  group?: string;
  boxKey?: string;
  variant?: "Small" | "PropertyLine";
  foldetUd: boolean;
  children: any;
}) {
  let [fold, setFold] = useState(props.foldetUd);

  useEffect(() => {
    setFold(props.foldetUd);
  }, [props.foldetUd]);

  return (
    <div className={VariantHandler("GlassFoldUdBox", props.variant) + (fold ? " open" : "")}>
      <div
        className="Head"
        onClick={() => setFold((a) => !a)}
      >
        {props.title}

        <div className="FoldCaret">
          <PiCaretRightLight />
        </div>
      </div>
      <div className="Body">{props.children}</div>
    </div>
  );
}

export function GlassBox(props: {
  title: any;
  group?: string;
  boxKey?: string;
  variant?: "Small";
  children: any;
}) {
  return (
    <div className={VariantHandler("GlassBox", props.variant) + (" open")}>
      <div
        className="Head"
      >
        {props.title}
      </div>
      <div className="Body">{props.children}</div>
    </div>
  );
}

export function GlassFoldUdBoxFalse(props: {
  title: any;
  group?: string;
  boxKey?: string;
  variant?: "Small";
}) {
  return (
    <div className={VariantHandler("GlassFoldUdBox", props.variant)}>
      <div
        className="Head"
      >
        {props.title}

        <div className="FoldCaret">
          {/* <PiCaretRightLight /> */}
        </div>
      </div>
    </div>
  );
}

export function ValueGroup(props: { children: any }) {
  return <div className="ValGroup-MitProp">{props.children}</div>;
}

export function SingleError(props: { error: { error: string } }) {
  console.error(props);
  return <div className="Error-MitProp">{props.error.error}</div>;
}

type GroupingChild = JSX.Element[] | JSX.Element | undefined;
export function ShowSelectedGroup(props: {
  keepSelected: boolean;
  loading: boolean;
  columns: number;
  group: string;
  favName?: string;
  favBoxes?: { [favName: string]: string[] }
  groupCallBack?: (groups: string[]) => any;
  children: GroupingChild[];
}) {
  let [validGroups, setValidGroups] = useState([""]);


  useEffect(() => {
    let x = (
      props.children.filter((a) => a) as NonNullable<GroupingChild>[]
    ).flatMap((a) => {
      const arrEle = Array.isArray(a) ? a : [a];
      return arrEle;
    });

    // Helper function to extract group prop, handling ProtectedFeature wrapper
    const getGroupProp = (element: any): string => {
      if (!React.isValidElement(element)) return "";
      // Check if this element has group prop directly
      if (element.props?.["group"]) return element.props["group"];
      // Check if this is ProtectedFeature and look at its children
      if (element.type === ProtectedFeatureComponent) {
        const children = (element.props as any)?.children;
        if (React.isValidElement(children) && (children.props as any)?.["group"]) {
          return (children.props as any)["group"];
        }
        // Handle array of children
        if (Array.isArray(children)) {
          for (const child of children) {
            if (React.isValidElement(child) && (child.props as any)?.["group"]) {
              return (child.props as any)["group"];
            }
          }
        }
      }
      return "";
    };

    let nextValidGroups = [
      ...new Set([
        ...x.map((a) => getGroupProp(a)),
      ]),
    ].filter((a) => a);

    if (nextValidGroups.every((a, idx) => a === validGroups[idx])) {
      return;
    }
    setValidGroups(nextValidGroups);
    props.groupCallBack?.(nextValidGroups);
  }, [props.children]);

  let x = (
    props.children.filter((a) => a) as NonNullable<GroupingChild>[]
  ).flatMap((a) => {
    const arrEle = Array.isArray(a) ? a : [a];
    return arrEle;
  });

  let activeGroup = validGroups.includes(props.group)
    ? props.group
    : (props.keepSelected ? props.group : validGroups[0]);

  // Helper function to check if element matches group, handling ProtectedFeature wrapper
  const matchesGroup = (element: any, targetGroup: string): boolean => {
    if (!React.isValidElement(element)) return false;
    // Check if this element has group prop directly
    if (element.props?.["group"] === targetGroup) return true;
    // Check if this is ProtectedFeature and look at its children
    if (element.type === ProtectedFeatureComponent) {
      const children = (element.props as any)?.children;
      if (React.isValidElement(children) && (children.props as any)?.["group"] === targetGroup) {
        return true;
      }
      // Handle array of children
      if (Array.isArray(children)) {
        return children.some((child) =>
          React.isValidElement(child) && (child.props as any)?.["group"] === targetGroup
        );
      }
    }
    return false;
  };

  let selectedElements =
    activeGroup == "Alle"
      ? x
      : x?.filter?.((a) => matchesGroup(a, activeGroup)) || [];



  if (props.group == "Favoritter") {
    if (props.favBoxes && props.favName && Object.keys(props.favBoxes).includes(props.favName)) {
      return (
        <ColumnSplitter columns={props.columns}>
          {props.favBoxes[props.favName].map((a) => {
            return (
              <Fragment key={a} >
                {x.filter((val) => val?.props?.["boxKey"] == a) || <></>}
              </Fragment>
            )
          })}
        </ColumnSplitter>
      )
    }
  }

  if (selectedElements.length == 0) {
    if (props.loading) {
      return (<PropertyLoading />)
    }
    return (
      <PropertyWarning
        title={Localization.getFormattedText("No information in {tab}", { tab: activeGroup })}
        subText={Localization.getText("Please select another group")}
      />
    )
  }


  return (
    <ColumnSplitter columns={props.columns}>
      {selectedElements}
    </ColumnSplitter>
  );
}

export function PropertyLoading() {
  return <div className="PropertyLoading">{Localization.getText("Loading")} <Spinner size="sm" /></div>
}

export function PropertyWarning(props: { title: string, subText: string }) {
  return <div className="PropertyWarning">
    <div>{props.title}</div>
    <div>{props.subText}</div>
  </div>
}

export function GroupSelector(props: {
  selected: string;
  group: string[];
  onSelect?: (group: string) => any;
}) {
  let thisRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (thisRef.current) {
      let element = thisRef.current as HTMLDivElement;
      const moveScrollEvent = (e: PointerEvent) => {
        let { left, right } = element.getBoundingClientRect();
        if (e.clientX < left || e.clientX > right) {
          return;
        }
        if (e.pointerType == "touch") {
          return;
        }
        element.scrollLeft =
          ((element.scrollWidth - element.clientWidth) * (e.clientX - left)) /
          element.clientWidth;
      };
      document.addEventListener("pointermove", moveScrollEvent);
      return () => document.removeEventListener("pointermove", moveScrollEvent);
    }
  }, []);

  function select(a: string) {
    props.onSelect?.(a);
  }

  return (
    <div
      ref={thisRef}
      className="GlassHorizontalScroll dont-print"
      style={{ scrollbarWidth: "none", maxHeight: "49px" }}
    >
      {props.group.map((a) => {
        return (
          <button
            key={a}
            className={(a === props.selected ? "selected" : "")}
            onClick={() => select(a)}
          >
            {a}
          </button>
        );
      })}
    </div>
  );
}

export function KeyValuesHorizontal(props: { children: any }) {
  return <div className="KeyValuesHorizontal">{props.children}</div>;
}

export function CenterGrid(props: { minWidth: `${number}px`; children }) {
  return (
    <div
      className="CenterGrid-MitProp"
      style={{ "--minBoxWidth": props.minWidth } as React.CSSProperties}
    >
      {props.children}
    </div>
  );
}

function useSplitterCols(
  smallestCol: number,
  min?: number,
  max?: number,
): [React.RefObject<HTMLDivElement>, number] {
  const container = useRef<HTMLDivElement>(null);
  const [cols, setCols] = useState(1)
  useEffect(() => {
    if (container.current) {
      const resizeObserver = new ResizeObserver((target) => {
        target.map((a) => {
          const calCols = a.borderBoxSize[0].inlineSize / smallestCol;
          setCols(Math.floor(Math.max(min ?? 1, Math.min(max ?? 4, calCols))))
        })
      })
      resizeObserver.observe(container.current)
      return () => {
        resizeObserver.disconnect()
      }
    }
  }, [container, container.current])

  return [container, cols]
}


/**
 *
 * @param props
 * @returns
 * @print Always only prints one collumn
 */
export function ColumnSplitter(props: {
  columns: number;
  children: any;
}) {

  const [container, cols] = useSplitterCols(600, 1, 2)
  let collumnsIDS = Array.from(Array(cols).keys());

  return (
    <div
      ref={container}
      className="Splitter-MitProp"
      style={{ "--forcedColumns": cols || 1 } as React.CSSProperties}
    >
      {collumnsIDS.map((colkey) => {
        return (
          <div key={colkey} className="SplitterSingle-MitProp">
            {props.children.filter((a, idx) => idx % cols == colkey)}
          </div>
        );
      })}
    </div>
  );
}

// ----------------------------- link components -------------------------

export function RenderLinkToPropertyInfo(text: string, props: { bfeNr: number }) {
  const { dispatch: windowDispatch } = useWindowContext();

  return (
    <a className="property-info-punit-link" onClick={() => {
      windowDispatch(openWindow(WindowId.PropertyInformationDialog, { bfeNr: props.bfeNr }));
    }}>{text}</a>
  );
}

export function RenderLinkToCompanyGraph(props: { cvrNr }) {
  const { dispatch: windowDispatch } = useWindowContext();
  return (
    <a className="property-info-punit-link" onClick={() => {
      windowDispatch(openWindow(WindowId.OwnerShipDiagram, { cvrNr: props.cvrNr }));
    }}>{props.cvrNr}</a>
  )
}

export function RenderLinkToPersonGraph(props: { cvrPersonId: number }) {
  const { dispatch: windowDispatch } = useWindowContext();
  return (
    <a className="property-info-punit-link" onClick={() => {
      windowDispatch(openWindow(WindowId.OwnerShipDiagram, { personInfo: { cvrPersonId: props.cvrPersonId } }))
    }}>{props.cvrPersonId}</a>
  )


}

// ---------------------------- Dialog Header Components ---------------

export function TooltipWrapper({
  children,
  title,
  className = "ViewButtonToolTip",
  ...props
}: {
  children: React.ReactElement,
  title?: string | React.ReactNode,
  className?: string
}) {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const { refs, floatingStyles, context: tooltipContext } = useFloating({
    whileElementsMounted: autoUpdate,
    open: tooltipOpen,
    middleware: [offset(5)],
    onOpenChange: setTooltipOpen,
  });

  const { getFloatingProps } = useInteractions([
    useHover(tooltipContext),
    useDismiss(tooltipContext)
  ]);

  return (
    <>
      {React.cloneElement(children, {
        ref: refs.setReference,
        ...props
      })}
      {Boolean(title && tooltipOpen) && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            className={className}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            {title}
          </div>
        </FloatingPortal>
      )}
    </>
  );
}

export function ViewButtonIconAsync({
  children,
  title,
  onClick,
  disabled,
  ...props }: ViewBTN) {
  const [working, setWorking] = useState(false);

  return (
    ViewButtonIcon({
      onClick: async (e) => {
        if (onClick) {
          setWorking(true);
          try {
            await onClick(e);
          } finally {
            setWorking(false);
          }
        }
      },
      children: working ? <Spinner style={{ "--viamap-green": "white" } as any} size="sm" /> : children,
      title,
      disabled: disabled || working,
      ...props
    }
    )

  )

}

type ViewBTN = {
  children: React.ReactNode
} & React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>
export function ViewButtonIcon({
  children,
  title,
  ...props }: ViewBTN) {


  const [tooltipOpen, setTooltipOpen] = useState(false);
  const { refs, floatingStyles, context: tooltipContext } = useFloating({
    whileElementsMounted: autoUpdate,
    // placement: hasAccessToFeature(Feature.ThemeNewSec) ? "bottom-end" : "top",
    open: tooltipOpen,
    middleware: [offset(5), autoPlacement({
      allowedPlacements: ["top", "top-start", "top-end", "bottom", "bottom-start", "bottom-end"]
    })],
    onOpenChange: setTooltipOpen,
  });
  const {
    getFloatingProps: getTooltipFloatingProps
  } = useInteractions([useHover(tooltipContext), useDismiss(tooltipContext)]);
  const useMergeRefsRef = useMergeRefs<HTMLButtonElement>([refs.setReference, props.ref].filter((a) => a) as React.RefObject<HTMLButtonElement>[]);

  return (
    <>
      <button {...props}
        ref={useMergeRefsRef}
        className={"ViewButton ViewButtonIcon " + props.className} >
        {children}
      </button>
      {Boolean(title && tooltipOpen) &&
        <FloatingPortal>
          <div ref={refs.setFloating} className="ViewButtonToolTip" style={floatingStyles} {...getTooltipFloatingProps()} >
            {title || "ThisIsToolTIp"}
          </div>
        </ FloatingPortal>
      }
    </>
  )
}

export function ViewButtonTextInput<T extends string | number>(props: {
  value?: T,
  onChange?: (e) => void,
  className?: string,

}) {
  const [val, setVal] = useState<string>("");

  function handleChage(e: React.ChangeEvent<HTMLInputElement>) {
    setVal(e.target.value || "");
  }

  useEffect(() => {

    setVal(props.value?.toString() || "")
  }, [props.value])

  return (
    <>
      <div className="ViewButton ViewButtonTextInput" >
        <input type="text" className={props.className} value={val} onChange={(e) => handleChage(e)} onBlur={() => props.onChange?.(val)} onKeyDown={(e) => e.key == "Enter" && props.onChange?.(val)} />
      </div>
    </>
  )
}

export function ViewButtonText({
  children,
  ...props }: ViewBTN) {
  return (
    <button {...props} className={"ViewButton ViewButtonText " + props.className} >
      {children}
    </button>
  )
}

export function ViewButtonCheck({
  children,
  checked,
  onCheck,
  ...props
}: ViewBTN & { checked: boolean, onCheck: (e, a) => void }) {

  function handleClick(e) {
    onCheck?.(e, checked)
  }

  return <>
    <button {...props} onClick={handleClick} className={"ViewButton ViewButtonCheck " + props.className}>
      <div className={"ViewButtonCheckBox " + (checked ? "checked" : "hidden")}>
        <BiCheck />
      </div>
      {children}
    </button>
  </>
}

export function ViewButtonCaret({
  children,
  isOpen,
  ...props }: ViewBTN & { isOpen: boolean }) {
  return (
    <button {...props} className={"ViewButton ViewButtonCaret " + props.className} >
      {children}
      <div className={"ViewButton-Carret " + (isOpen ? "open" : "closed")}>
        <BsCaretRightFill></BsCaretRightFill>
      </div>
    </button>
  )
}

export function ViewButtonGroup({
  children
}: { children: React.ReactNode }) {
  return (
    <div className="ViewButtonGroup">
      {children}
    </div>
  )
}

export function ViewButtonFlex({ children, title, ...props }: ViewBTN) {

  const [tooltipOpen, setTooltipOpen] = useState(false);
  const { refs, floatingStyles, context: tooltipContext } = useFloating({
    whileElementsMounted: autoUpdate,
  });
  const {
    getFloatingProps: getTooltipFloatingProps
  } = useInteractions([useHover(tooltipContext), useDismiss(tooltipContext)]);

  return (
    <>
      <button {...props} ref={refs.setReference} className={"ViewButton ViewButtonFlex " + props.className} >
        {children}
      </button>
      {Boolean(title && tooltipOpen) &&
        <FloatingPortal>
          <div ref={refs.setFloating} className="ViewButtonToolTip" style={floatingStyles} {...getTooltipFloatingProps()} >
            {title || "ThisIsToolTIp"}
          </div>
        </ FloatingPortal>
      }
    </>
  )
}

export function ViewButtonPopDown({ children, placement = "bottom-end", popdown, open, ...props }: ViewBTN & { popdown: React.ReactNode, placement?: Placement, open: boolean }) {
  const { refs, floatingStyles } = useFloating({
    whileElementsMounted: autoUpdate,
    placement: placement,
  });

  const [tooltipOpen, setTooltipOpen] = useState(false);
  const { refs: refsTT, floatingStyles: floatingStylesTT, context: tooltipContext } = useFloating({
    whileElementsMounted: autoUpdate,
    open: tooltipOpen,
    middleware: [offset(5)],
    onOpenChange: setTooltipOpen,
  });
  const {
    getFloatingProps: getTooltipFloatingProps
  } = useInteractions([useHover(tooltipContext), useDismiss(tooltipContext)]);


  const refReference = useMergeRefs([refs.setReference, refsTT.setReference])
  return (
    <>
      <button className={"ViewButton ViewButtonPopDown " + (open ? "open" : "closed") + props.className} ref={refReference} {...props}>
        {children}
      </button>
      {Boolean(props.title && tooltipOpen) &&
        <FloatingPortal>
          <div ref={refsTT.setFloating} className="ViewButtonToolTip" style={floatingStylesTT} {...getTooltipFloatingProps()} >
            {props.title || "ThisIsToolTIp"}
          </div>
        </ FloatingPortal>
      }
      {
        open ? (
          <FloatingPortal  >
            <div ref={refs.setFloating} className="ViewButtonPopDownBox" style={{ zIndex: "1001", ...floatingStyles }} >
              {popdown}
            </div>
          </FloatingPortal>
        ) : null}
    </>
  )
}

export function ViewButtonDropdown({ children, options, onSelect, hideEmpty, ...props }: ViewBTN & { options: { text: string, value: any }[], onSelect: (a) => void, hideEmpty?: boolean }) {
  const [showOptions, setShowOptions] = useState<boolean>(false)
  const { refs, floatingStyles } = useFloating({
    whileElementsMounted: autoUpdate,
    placement: "bottom-end",
  });

  if (hideEmpty && options.length == 0) {
    return null;
  }

  return (
    <>
      <button className={"ViewButton ViewButtonCaret " + props.className} ref={refs.setReference} onClick={() => setShowOptions((a) => !a)}>
        {children}
        <div className="ViewButton-Carret">
          {!showOptions ? <BsCaretDown /> : <BsCaretDownFill />}
        </div>
      </button>
      {
        showOptions ? (
          <FloatingPortal  >
            <div ref={refs.setFloating} className="ViewButtonDropDown" style={{ zIndex: "1001", ...floatingStyles }} >
              {options.map((a) => {
                return (
                  <button key={a.value} onClick={() => { onSelect(a.value); setShowOptions(false) }}>{a.text}</button>
                )
              })}
            </div>
          </FloatingPortal>
        ) : null}
    </>
  )
}

export function ViewButtonCollapser({ children }: { children: React.ReactNode[] }) {
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const { hasAccessToFeature } = useContext(ApplicationStateContext)

  const burgerMenuId = useId();
  const { refs, floatingStyles } = useFloating({
    open: showMenu,
    onOpenChange: setShowMenu,
    whileElementsMounted: autoUpdate,
    placement: "bottom-end",
  });
  const sizingRef = useRef<HTMLDivElement>(null);
  const [elementToShow, setElementToShow] = useState<number>(0);

  let elementArray = React.Children.map(children, (child) => {
    if (React.isValidElement(child) && child.props.feature) {
      return hasAccessToFeature(child.props.feature) ? child : null;
    }
    return child;
  });
  const totalElements = elementArray?.filter(el => el !== null).length || 0;

  useEffect(() => {
    function updateVisibleChildren() {
      if (!sizingRef.current) return;
      let allowedWidth = sizingRef.current.offsetWidth - 26 - 4; // 26px for the menu button
      let count = 0;
      for (const child of Array.from(sizingRef.current.childNodes)) {
        if (child instanceof HTMLElement) {
          if (allowedWidth < (child.offsetWidth + child.offsetLeft + 4)) {
            break;
          } else {
            count++;
          }
        }
      }
      if (totalElements - count === 1) {
        count = totalElements;
      }
      setElementToShow((a) => {
        if (a !== count)
          console.log("Setting element to show to ", count);
        return count;
      });
    }

    updateVisibleChildren();

    const observer = new ResizeObserver(() => {
      updateVisibleChildren();
    });
    const refCurrent = sizingRef.current;
    if (refCurrent) {
      observer.observe(refCurrent);
    }
    return () => {
      observer.disconnect();
    };
  }, [children, totalElements]);

  // let elementArray = React.Children.map(children, (child) => { return child });
  let showOnList = elementArray?.filter((_a, idx) => idx < elementToShow);
  let showInMenu = elementArray?.filter((_a, idx) => idx >= elementToShow);

  useEffect(() => {
    if (showMenu) {
      const closeOutside = (ev: PointerEvent) => {
        const elem = document.getElementById(burgerMenuId)
        if (elem?.contains(ev.target as HTMLElement)) {
          return
        }
        setShowMenu(false)
        document.removeEventListener('pointerdown', closeOutside)
      }
      document.addEventListener('pointerdown', closeOutside);
      document.removeEventListener('pointerdown', closeOutside)
    }
  }, [showMenu])

  return (
    <>
      <div className="ViewButtonCollapser" >
        <div className="_simulator" ref={sizingRef}>{children}</div>
        <div className="_listview" >
          {showOnList}
          {(showInMenu?.length || 0) > 0 &&
            <div ref={refs.setReference} >
              <ViewButtonIcon title={Localization.getText("Show more")} onClick={() => setShowMenu((prev) => !prev)}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <Menu />
              </ViewButtonIcon>
            </div>
          }
        </div>
      </div>
      {(showInMenu?.length || 0) > 0 && showMenu &&
        <FloatingPortal  >
          <div ref={refs.setFloating} className="ViewButtonCollapseMenu" style={{ zIndex: 1001, ...floatingStyles }} id={burgerMenuId} >
            {React.Children.map(showInMenu, (child) => {
              if (React.isValidElement(child) && (child as any).props.title) {
                return <div className="ViewButton items" onClick={(e) => { setShowMenu(false); (child as any).props.onClick(e) }}>{(child as any).props.title}</div>
              } if (React.isValidElement(child) && "feature" in (child as any).props) {
                return <ProtectedFeatureComponent feature={(child as any).props.feature} contentsIfNoAccess={<></>}>
                  {React.Children.map((child as any).props.children, (ch: any) => {
                    if (React.isValidElement(ch) && (ch as any).props.title) {
                      return <div className="ViewButton items" onClick={(e) => { setShowMenu(false); (ch as any).props.onClick(e) }}>{(ch as any).props.title}</div>
                    }
                  })}
                </ProtectedFeatureComponent>
              }
              console.log(child);
              return child;
            })
            }
          </div>
        </FloatingPortal>
      }
    </>
  );
}

export function RenderLinkToCompanyInfo(
  props: {
    children: React.ReactNode
    cvrNr: number,
  }
) {
  const { dispatch: windowDispatch } = useWindowContext();
  return (
    <a className="property-info-punit-link" onClick={() => {
      windowDispatch(openWindow(WindowId.CompanyInformationDialog, props))
    }}>{props.children}</a>
  );
}

export function RenderLinkToCondominium(
  props: {
    children: React.ReactNode,
    bfeNr: number | string,
  }) {
  const { dispatch: windowDispatch } = useWindowContext();
  return (
    <a className="property-info-punit-link" onClick={() => {
      windowDispatch(openWindow(WindowId.CondominiumDialog, props))
    }}>{props.children}</a>
  )
}

export function RenderLinkToPersonInfo(
  props: {
    children: React.ReactNode,
    name: string,
    dateOfBirth?: Date,
    adresse: string,
    andelProcent: number,
    displayText: string,
    postdistrikt: string,
    postnr: number,
    cvrPersonId?: number,
  }
) {
  const { dispatch: windowDispatch } = useWindowContext();
  return (
    <a className="property-info-punit-link" onClick={() => {
      windowDispatch(openWindow(WindowId.PersonInformationDialog, { ...props }))
    }}>{props.children}</a>
  );
}